import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  LuLoader,
  LuCircleCheck,
  LuTriangleAlert,
  LuRefreshCw,
  LuArrowLeft,
} from 'react-icons/lu';
import Icon from '../../components/common/Icon';
import { subscriptionApi } from '../../api/subscription-api';
import { useTheme } from '../../context/ThemeContext';

const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 24; // 60 seconds total

export const SubscriptionSuccessPage: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const txRef = searchParams.get('tx_ref');

  const [status, setStatus] = useState<'polling' | 'active' | 'timeout' | 'error'>('polling');
  const [message, setMessage] = useState('Confirming your payment…');
  const pollCount = useRef(0);

  useEffect(() => {
    if (!txRef) {
      setStatus('error');
      setMessage(
        'No transaction reference found. If you completed payment, please contact support.'
      );
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;

      try {
        // Trigger server-side verification on every attempt until subscription is active.
        // Paychangu may take a few seconds to finalize the payment.
        await subscriptionApi.verify(txRef).catch(() => {});

        const res = await subscriptionApi.getStatus();
        if (res.data?.has_subscription) {
          setStatus('active');
          setMessage('Payment confirmed! Redirecting you to the dashboard…');
          setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
          return;
        }
      } catch {
        // silently continue polling
      }

      if (cancelled) return;
      pollCount.current += 1;
      if (pollCount.current >= MAX_POLLS) {
        setStatus('timeout');
        setMessage(
          'Payment is taking longer than expected. If you were charged, please contact support — your access will be activated shortly.'
        );
        return;
      }

      setTimeout(poll, POLL_INTERVAL_MS);
    };

    setTimeout(poll, 1500);
    return () => { cancelled = true; };
  }, [txRef, navigate]);

  return (
    <div className={isDark ? 'dark' : ''}>
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-[#0b1120] px-4">

        <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-100 dark:border-slate-800 overflow-hidden">
          <div className="h-1 w-full bg-gradient-to-r from-primary via-primary-dark to-[#6BBBB1]" />

          <div className="p-10 flex flex-col items-center text-center">
            <img
              src={isDark ? '/clicklawWhite.svg' : '/clickLawBlaq.svg'}
              alt="ClickLaw"
              className="h-8 w-auto mb-8"
            />

            {/* Status icon */}
            {status === 'polling' && (
              <div className="w-16 h-16 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center mb-6">
                <Icon
                  icon={LuLoader}
                  size={28}
                  className="text-primary dark:text-primary-dark animate-spin"
                />
              </div>
            )}

            {status === 'active' && (
              <div className="w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center mb-6">
                <Icon icon={LuCircleCheck} size={32} className="text-emerald-500" />
              </div>
            )}

            {(status === 'timeout' || status === 'error') && (
              <div className="w-16 h-16 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center mb-6">
                <Icon icon={LuTriangleAlert} size={28} className="text-amber-500" />
              </div>
            )}

            <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              {status === 'polling' && 'Processing payment'}
              {status === 'active' && 'Subscription activated!'}
              {status === 'timeout' && 'Still confirming…'}
              {status === 'error' && 'Something went wrong'}
            </h1>

            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed mb-8">
              {message}
            </p>

            {status === 'polling' && (
              <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-1.5 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-primary to-primary-dark rounded-full animate-pulse"
                  style={{ width: '60%' }}
                />
              </div>
            )}

            {(status === 'timeout' || status === 'error') && (
              <div className="flex flex-col gap-3 w-full">
                <button
                  onClick={() => {
                    pollCount.current = 0;
                    setStatus('polling');
                    setMessage('Confirming your payment…');
                  }}
                  className="w-full rounded-2xl bg-primary text-white font-semibold py-3 text-sm hover:bg-primary-ink transition-colors shadow-lg shadow-primary/20 flex items-center justify-center gap-2"
                >
                  <Icon icon={LuRefreshCw} size={14} />
                  Check again
                </button>
                <button
                  onClick={() => navigate('/dashboard/subscription/required', { replace: true })}
                  className="w-full rounded-2xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-semibold py-3 text-sm hover:border-slate-300 dark:hover:border-slate-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Icon icon={LuArrowLeft} size={14} />
                  Back to payment
                </button>
              </div>
            )}

            {txRef && status !== 'active' && (
              <p className="mt-6 text-xs text-slate-400 dark:text-slate-600 font-mono">
                Ref: {txRef}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
